import type { SortingState } from "@tanstack/react-table";
import type { ArchivedMode, FilterValue, ListParams, SectionPreset } from "./types";

/**
 * URL search params for an entity list. Flat so links stay readable and shareable:
 *   ?page=2&size=50&sort=-requested_at&q=printer&archived=all
 *   &f.status=open,in_progress&f.requested_at.gte=2026-01-01&xp=assigned_to_id
 * `xp` lists preset filters the user removed. `new=1` + `prefill.<field>=…` opens the create form.
 * Saved views add `view=<id>` (the selected view) and `cols=a,b,c` (visible columns, in order).
 */
export type ListSearch = Record<string, string | number | boolean | undefined>;

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZES = [25, 50, 100, 200];

export function validateListSearch(raw: Record<string, unknown>): ListSearch {
  const out: ListSearch = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") out[key] = value;
  }
  return out;
}

const str = (v: unknown): string | undefined => (v === undefined || v === null || v === "" ? undefined : String(v));
export const splitList = (v: unknown): string[] =>
  str(v)
    ?.split(",")
    .map((s) => s.trim())
    .filter(Boolean) ?? [];

export function toList(value: FilterValue | undefined): string[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

export interface ListState {
  page: number;
  pageSize: number;
  sort: string | undefined;
  q: string;
  archived: ArchivedMode;
  /** Filters chosen in the UI (from `f.*`). */
  filters: Record<string, string[]>;
  /** Preset filters still in force. */
  presetFilters: Record<string, string[]>;
  excludedPresets: string[];
  /** Columns chosen in the UI (`cols`), raw; empty = entity default. */
  columns: string[];
  /** Selected saved view id (`view`). */
  view: string | undefined;
}

export function readListState(search: ListSearch, defaultSort: string | null | undefined, preset?: SectionPreset | null): ListState {
  const page = Math.max(1, Math.floor(Number(search.page) || 1));
  const sizeNum = Number(search.size);
  const pageSize = PAGE_SIZES.includes(sizeNum) ? sizeNum : DEFAULT_PAGE_SIZE;
  const filters: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(search)) {
    if (!key.startsWith("f.")) continue;
    const values = splitList(value);
    if (values.length) filters[key.slice(2)] = values;
  }
  const excludedPresets = splitList(search.xp);
  const presetFilters: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(preset?.filters ?? {})) {
    if (excludedPresets.includes(key) || filters[key]) continue;
    const values = toList(value);
    if (values.length) presetFilters[key] = values;
  }
  const archivedRaw = str(search.archived);
  const archived: ArchivedMode = archivedRaw === "true" || archivedRaw === "all" ? archivedRaw : "false";
  return {
    page,
    pageSize,
    sort: str(search.sort) ?? preset?.sort ?? defaultSort ?? undefined,
    q: str(search.q) ?? "",
    archived,
    filters,
    presetFilters,
    excludedPresets,
    columns: splitList(search.cols),
    view: str(search.view),
  };
}

export function toListParams(state: ListState): ListParams {
  return {
    page: state.page,
    page_size: state.pageSize,
    sort: state.sort,
    search: state.q || undefined,
    archived: state.archived,
    filters: { ...state.presetFilters, ...state.filters },
    columns: state.columns.length ? state.columns : undefined,
  };
}

export function sortToState(sort: string | undefined): SortingState {
  if (!sort) return [];
  const first = sort.split(",")[0]?.trim();
  if (!first) return [];
  return first.startsWith("-") ? [{ id: first.slice(1), desc: true }] : [{ id: first, desc: false }];
}

export function stateToSort(sorting: SortingState): string | undefined {
  const s = sorting[0];
  if (!s) return undefined;
  return s.desc ? `-${s.id}` : s.id;
}

/** URL patch for a filter change; resets to page 1. */
export function filterPatch(key: string, values: string[]): ListSearch {
  return { [`f.${key}`]: values.length ? values.join(",") : undefined, page: undefined };
}

/** Prefill values from `prefill.<field>` params. */
export function readPrefill(search: ListSearch): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(search)) {
    if (key.startsWith("prefill.") && value !== undefined && value !== "") out[key.slice(8)] = String(value);
  }
  return out;
}

/** Drop undefined keys so URLs stay tidy. */
export function cleanSearch(search: ListSearch): ListSearch {
  const out: ListSearch = {};
  for (const [k, v] of Object.entries(search)) if (v !== undefined && v !== "") out[k] = v;
  return out;
}
